import axios from 'axios';
import { demoAdapterOptions, isPlatformDemo } from '../demo/runtime.js';
import { normalizePersonDisplayNames } from '../utils/personName.js';
import { browserSessionOptions, createSessionCoordinator } from './sessionCoordinator.js';

const baseURL = import.meta.env?.VITE_API_URL || '/api/messenger';
let accessToken = null;
let refreshPromise = null;
let sessionListener = null;
const sessions = createSessionCoordinator({
  ...browserSessionOptions(baseURL, !isPlatformDemo()),
  onChange(data) {
    normalizePersonDisplayNames(data);
    setAccessToken(data?.accessToken);
    sessionListener?.(data);
  }
});

export const api = axios.create({
  ...demoAdapterOptions(),
  baseURL,
  withCredentials: true,
  timeout: 30_000,
  responseType: 'json',
  transitional: { silentJSONParsing: false }
});

const refreshClient = axios.create({
  ...demoAdapterOptions(),
  baseURL,
  withCredentials: true,
  timeout: 15_000,
  responseType: 'json',
  transitional: { silentJSONParsing: false }
});

export function setAccessToken(token) {
  accessToken = token || null;
}

export function getAccessToken() {
  return accessToken;
}

export function onSessionChange(listener) {
  sessionListener = listener;
}

export function sessionUserId() {
  return sessions.current()?.user?.id || null;
}

export function changeSession(action, options) {
  return sessions.change(action, options);
}

export async function refreshSession(attemptedToken = accessToken) {
  if (!refreshPromise) {
    refreshPromise = sessions.refresh(async () => {
      const { data } = await refreshClient.post('/auth/refresh', null, { skipAuthRefresh: true });
      return data;
    }, attemptedToken)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

api.interceptors.request.use((config) => {
  if (!Object.hasOwn(config, '_sessionUserId')) config._sessionUserId = sessionUserId();
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

api.interceptors.response.use(
  (response) => {
    normalizePersonDisplayNames(response.data);
    return response;
  },
  async (error) => {
    const original = error.config;
    const temporaryFailure = !error.response || [408, 502, 503, 504].includes(error.response.status)
      || (error.cause instanceof SyntaxError && error.response.status >= 200 && error.response.status < 300);
    const sameSession = original?.headers?.Authorization === (accessToken ? `Bearer ${accessToken}` : undefined);
    // Retry reads once after a transient disconnect. Never replay a message,
    // login, upload, or other mutation whose response may have been lost.
    if (original?.method === 'get' && !original._networkRetry && temporaryFailure
      && !axios.isCancel(error) && !original.signal?.aborted && sameSession) {
      original._networkRetry = true;
      await new Promise(resolve => setTimeout(resolve, 500));
      if (!original.signal?.aborted
        && original.headers?.Authorization === (accessToken ? `Bearer ${accessToken}` : undefined)) return api(original);
      throw error;
    }
    const isUnauthorized = error.response?.status === 401;
    if (!isUnauthorized || original?._retry || original?.skipAuthRefresh) throw error;
    if (original._sessionUserId !== sessionUserId()) throw error;
    original._retry = true;
    const credential = original.headers?.Authorization?.replace(/^Bearer /, '') || null;
    const session = await refreshSession(credential);
    if (!session?.accessToken || (original._sessionUserId && original._sessionUserId !== sessionUserId())) throw error;
    original.headers.Authorization = `Bearer ${session.accessToken}`;
    return api(original);
  }
);

export function errorMessage(error, fallback = 'Не удалось выполнить действие') {
  if (!navigator.onLine) return 'Нет соединения с интернетом';
  return error?.response?.data?.error?.message || error?.message || fallback;
}

export function uploadUrl(attachmentId) {
  return `${baseURL}/uploads/${attachmentId}`;
}
